// A small list kept in this browser (localStorage): favourites and recents. Every read and write is wrapped in
// try/catch, so a blocked or full storage means an empty or unsaved list, never an error on screen.
import { useSyncExternalStore } from "react";

type Store = Pick<Storage, "getItem" | "setItem">;

export type LocalList<T> = {
  /** The current list, newest first. */
  get(): T[];
  /** Puts `item` first, drops an equal item, and caps the list at its limit. Returns the new list. */
  push(item: T): T[];
  /** Removes every item equal to `item`. Returns the new list. */
  remove(item: T): T[];
  /** Adds `item` when absent and removes it when present. Returns the new list. */
  toggle(item: T): T[];
  has(item: T): boolean;
  /** React hook: re-renders its component whenever the list changes. */
  useList(): T[];
  /** Forgets the in-memory copy so the next read comes from storage again. */
  reload(): void;
  /** Replaces the whole list (invalid items dropped, cut to the limit) and tells every listener. Returns the new list. */
  replace(items: T[]): T[];
};

/** Builds a list stored under `key`. `storage` defaults to localStorage; tests pass their own. */
export function createLocalList<T>(opts: {
  key: string;
  limit: number;
  same: (a: T, b: T) => boolean;
  isItem: (value: unknown) => value is T;
  storage?: () => Store | undefined;
}): LocalList<T> {
  const storage = opts.storage ?? defaultStorage;
  const listeners = new Set<() => void>();
  const none: T[] = [];
  let current: T[] | null = null;

  const read = (): T[] => {
    try {
      const raw = storage()?.getItem(opts.key);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(opts.isItem).slice(0, opts.limit) : [];
    } catch {
      return [];
    }
  };
  const get = () => {
    current ??= read();
    return current;
  };
  const write = (next: T[]): T[] => {
    current = next.slice(0, opts.limit);
    try {
      storage()?.setItem(opts.key, JSON.stringify(current));
    } catch {
      // storage is full or blocked: the in-memory list still works for this page view
    }
    for (const listener of listeners) listener();
    return current;
  };
  const has = (item: T) => get().some((x) => opts.same(x, item));
  const push = (item: T) => write([item, ...get().filter((x) => !opts.same(x, item))]);
  const remove = (item: T) => write(get().filter((x) => !opts.same(x, item)));
  const toggle = (item: T) => (has(item) ? remove(item) : push(item));
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  return {
    get,
    push,
    remove,
    toggle,
    has,
    replace: (items) => write(items.filter(opts.isItem)),
    useList: () => useSyncExternalStore(subscribe, get, () => none),
    reload: () => {
      current = null;
    },
  };
}

function defaultStorage(): Store | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
